import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Card } from '../ui/card';
import { FilesProcessedReport, userFilesReportService } from '../../services/UserFilesReportService';

const UserFilesProcessedBarChart: React.FC = () => {
  const [reportData, setReportData] = useState<FilesProcessedReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await userFilesReportService.refreshReportData();
      setLoading(false);
    };
    
    fetchData();
    
    // Subscribe to future updates
    const unsubscribe = userFilesReportService.subscribe((data) => {
      setReportData(data);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Configure chart options
  const getChartOptions = (): ApexOptions => {
    return {
      chart: {
        fontFamily: "Outfit, sans-serif",
        type: "bar",
        stacked: false,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 5,
          borderRadiusApplication: "end",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: [0, 2],
        curve: 'smooth',
        colors: ['transparent', "#4ade80"]
      },
      colors: ['#465fff', '#4ade80'],
      xaxis: {
        categories: reportData?.weeklySummary.map(week => week.weekLabel) || [],
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: [
        {
          title: {
            text: 'Files Processed',
            style: {
              fontWeight: 400,
            }
          },
          labels: {
            formatter: function (val) {
              return val.toFixed(0);
            }
          }
        },
        {
          opposite: true,
          title: {
            text: 'File Size (KB)',
            style: {
              fontWeight: 400,
            }
          },
          labels: {
            formatter: function (val) {
              return val.toFixed(0);
            }
          }
        }
      ],
      tooltip: {
        shared: true,
        intersect: false,
        y: [
          {
            formatter: function (val) {
              return val.toFixed(0) + " files";
            }
          },
          {
            formatter: function (val) {
              return val.toFixed(0) + " KB";
            }
          }
        ]
      },
      legend: {
        show: true,
        position: 'bottom',
        offsetY: 7,
        fontSize: '14px',
        fontFamily: 'Outfit, sans-serif',
        horizontalAlign: 'center',
        markers: {
          size: 10,
          strokeWidth: 0,
        }
      },
      grid: {
        borderColor: '#e5e7eb',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 10
        }
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            yaxis: {
              show: false
            }
          }
        }
      ]
    };
  };
  
  // Prepare chart data
  const getChartSeries = () => {
    if (!reportData) {
      return [
        {
          name: 'Files Processed',
          type: 'column',
          data: []
        },
        {
          name: 'File Size (KB)',
          type: 'line',
          data: []
        }
      ];
    }
    
    return [
      {
        name: 'Files Processed',
        type: 'column',
        data: reportData.weeklySummary.map(week => week.filesCount)
      },
      {
        name: 'File Size (KB)',
        type: 'line',
        data: reportData.weeklySummary.map(week => Math.round(week.totalSizeKB))
      }
    ];
  };
  
  // Handle loading state
  if (loading && !reportData) {
    return (
      <Card className="p-5 h-full">
        <div className="flex justify-center items-center h-80">
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-52 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-5 h-full">
      <div className="flex flex-col h-full">
        <div className="flex flex-col space-y-1">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Files Processed</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {reportData?.currentMonth || 'Current Month'}
          </p>
        </div>
        
        <div className="mt-4 flex-1 min-h-[320px]">
          <Chart 
            options={getChartOptions()} 
            series={getChartSeries()} 
            type="line"
            height={320}
          />
        </div>
        
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Files Processed</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {reportData?.totalFilesProcessed || 0}
            </p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Size</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {reportData ? Math.round(reportData.totalSizeKB) : 0} KB
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default UserFilesProcessedBarChart;